/**
 * 매뉴얼 문구 — `landing` 네임스페이스의 `site.manual` 로 합쳐진다(service-manual: 매뉴얼 문구의 출처).
 * 꾸밈: `<ui>…</ui>` 화면의 단추·칸 이름, `<a href="#t-…">…</a>` 다른 절로 가는 링크. 절 구조·안내 화면 좌표는
 * `Home/Manual/manualTopics.ts`·`manualGuides.ts` 에 있다.
 */
const landingManual = {
  "eyebrow": "Manual",
  "title": "Learn DroneSquare with video guides",
  "searchLabel": "Search the manual",
  "searchPlaceholder": "Enter a feature to find (e.g. mapping, cross-section)",
  "searchEmpty": "No matching features. Try another word.",
  "searchCount": "Sections found: {{count}}",
  "chaptersLabel": "Guides by feature",
  "jumpLabel": "Jump to a manual chapter",
  "tocLabel": "Sections in {{chapter}}",
  "allChapters": "All chapters",
  "backToStart": "Back to start",
  "gate": {
    "title": "Log in to view the manual and demo videos",
    "text": "Log in with your DroneSquare account, or contact us if you don't have one yet.",
    "login": "Login",
    "inquiry": "Contact us"
  },
  "noteLabel": {
    "note": "Note",
    "caution": "Caution"
  },
  "player": {
    "label": "Step-by-step guide: {{title}}",
    "stepsLabel": "Go to step",
    "step": "Step {{n}}: {{title}}",
    "prev": "Previous step: {{title}}",
    "next": "Next step: {{title}}",
    "play": "Play",
    "pause": "Pause"
  },
  "chapters": {
    "c1": {
      "name": "Projects",
      "subtitle": "Manage projects and capture dates"
    },
    "c2": {
      "name": "Images & mapping",
      "subtitle": "Upload photos and turn them into maps"
    },
    "c3": {
      "name": "Reference & layer data",
      "subtitle": "Upload drawings and documents together"
    },
    "c4": {
      "name": "2D inspection",
      "subtitle": "Inspect sites with orthomosaics",
      "open": "2D inspection opens when you click <ui>Time Series Inspection</ui> in the sub-table menu of a processed capture date, or <ui>2d3d</ui> on the left rail."
    },
    "c5": {
      "name": "3D inspection",
      "subtitle": "Inspect sites with 3D models",
      "open": "3D inspection opens the 3D model of the same capture date when you click <ui>3D</ui> on the rail. If the 3D model hasn't been built yet, \"The 3D mesh hasn't been built yet\" appears."
    },
    "c6": {
      "name": "Section graphs",
      "subtitle": "View longitudinal and cross sections"
    },
    "c7": {
      "name": "Site monitoring",
      "subtitle": "Monitor workers and equipment on the map",
      "open": "Site monitoring is entered from <ui>worksite</ui> on the rail of the project list. It opens only when at least one capture date has finished 2D or 3D processing."
    },
    "c8": {
      "name": "Earthwork",
      "subtitle": "Calculate earthwork by zone and alignment",
      "open": "Earthwork is entered from <ui>earthwork</ui> on the rail when the project has a drawing for area-based earthwork or a line layer, and a capture date that has finished processing."
    }
  },
  "topics": {
    "t-new": {
      "title": "Create a new project",
      "note": "The list's \"Filter\" works on tags only, so add the groupings you'll want to find later as tags. Closing the window discards what you entered. You can change the location, name, types, tags and memo later in <a href=\"#t-edit\">Edit project information</a>.",
      "steps": [
        {
          "title": "Click \"Create New Project\"",
          "caption": "Click \"Create New Project\" at the top right."
        },
        {
          "title": "Start the location step",
          "caption": "The \"Add Project\" window opens. Step 1, Location: search for an address or click the map."
        },
        {
          "title": "Choose the site address",
          "caption": "When the search results appear, choose the site address."
        },
        {
          "title": "Check location, click \"Next\"",
          "caption": "The map moves to that location, and the card below shows the address, latitude and longitude. Click \"Next\"."
        },
        {
          "title": "Enter project info",
          "caption": "Step 2, Project info: enter the project name, infrastructure type, object type, tags and memo."
        },
        {
          "title": "Open \"Infrastructure Type\"",
          "caption": "The project name is entered. Choose an \"Infrastructure Type\"."
        },
        {
          "title": "Choose the object kind",
          "caption": "Choose from general, corridor, surface and vertical objects. For \"Others\", type your own."
        },
        {
          "title": "Enter \"Object Type\"",
          "caption": "Enter the \"Object Type\"."
        },
        {
          "title": "Add tags and memo, click \"Next\"",
          "caption": "Tags and memo are optional. Click \"Next\"."
        },
        {
          "title": "Open the capture date field",
          "caption": "Step 3, Add Time Series: click the capture date field and choose the first capture date."
        },
        {
          "title": "Pick a date on the calendar",
          "caption": "Pick a date on the calendar."
        },
        {
          "title": "Click \"Add\" to create",
          "caption": "The capture date is filled in. Click \"Add\" to create the project."
        },
        {
          "title": "Check the project is created",
          "caption": "\"Project created successfully\" appears and you return to the list."
        }
      ]
    },
    "t-edit": {
      "title": "Edit project information",
      "note": "Capture dates and their memos are edited in <a href=\"#t-series\">Add, edit, delete time series</a>, not in this window.",
      "steps": [
        {
          "title": "Select a project",
          "caption": "In the list, click the project you want to edit."
        },
        {
          "title": "Click \"Edit\"",
          "caption": "Click \"Edit\" next to \"Details\" in the \"Information\" tab on the right."
        },
        {
          "title": "Edit the fields",
          "caption": "The \"Edit Project Information\" window opens. Edit any of the name, types, tags or memo."
        },
        {
          "title": "Click \"Save\"",
          "caption": "After editing, click \"Save\". \"Reset\" restores the original values."
        },
        {
          "title": "Confirm the update",
          "caption": "The \"Project information has been updated\" notice appears, and the details update right away."
        }
      ]
    },
    "t-series": {
      "title": "Add, edit, delete time series",
      "note": "Only the project owner can add or edit capture dates.",
      "steps": [
        {
          "title": "Click \"Add Time Series\"",
          "caption": "Click \"Add Time Series\" in the table."
        },
        {
          "title": "Open the capture date field",
          "caption": "This is the \"Add Time Series\" window. Click the capture date field to pick a date."
        },
        {
          "title": "Pick the capture date",
          "caption": "Pick the capture date on the calendar. A date that already exists cannot be created."
        },
        {
          "title": "Enter a memo",
          "caption": "The capture date is set. In the memo field, describe this time series."
        },
        {
          "title": "Click \"Completed\"",
          "caption": "Enter the memo and click \"Completed\"."
        },
        {
          "title": "Expand the date list",
          "caption": "The new capture date is added. Click the arrow at the left of the project row to expand its capture dates."
        },
        {
          "title": "Select the date to edit",
          "caption": "The new capture date row appears. Click the capture date you want to edit."
        },
        {
          "title": "Click \"Edit\"",
          "caption": "Click \"Edit\" next to \"$t(projects:timeSeries.mappingInfo)\" in the \"Information\" tab on the right."
        },
        {
          "title": "Edit the memo",
          "caption": "In the \"Edit Time Series\" window, edit the capture date and memo."
        },
        {
          "title": "Click \"Save\"",
          "caption": "The memo is edited. Click \"Save\"."
        },
        {
          "title": "Click the trash icon",
          "caption": "The \"Time series information has been updated\" notice appears. To delete, click the trash icon on that row."
        },
        {
          "title": "Type the date exactly",
          "caption": "This is the \"Delete Time Series\" window. Type the date exactly as shown in the confirmation field."
        },
        {
          "title": "Click \"Delete\"",
          "caption": "\"Delete\" turns on only when the dates match. A deleted capture date cannot be restored."
        },
        {
          "title": "Confirm the deletion",
          "caption": "The deletion notice appears and the capture date disappears from the list."
        }
      ]
    },
    "t-list": {
      "title": "Project list",
      "note": "The \"Drone Images\" tab appears only when a capture date is selected.",
      "steps": [
        {
          "title": "Search by name",
          "caption": "The project list opens when you log in. Type part of a name in the search box."
        },
        {
          "title": "Clear the search",
          "caption": "After a moment only matching projects remain. Clear the search to return to the full list."
        },
        {
          "title": "Open \"Filter\"",
          "caption": "From the full list, click \"Filter\" to narrow it down by tag."
        },
        {
          "title": "Pick a tag",
          "caption": "Check the tag you want. The number beside it is how many projects have that tag."
        },
        {
          "title": "Click \"Apply\"",
          "caption": "Click \"Apply\". \"Reset\" clears all checks."
        },
        {
          "title": "Select a project",
          "caption": "Only projects with that tag remain in the table and on the map. Click a project."
        },
        {
          "title": "Switch data tabs",
          "caption": "The map moves to its location and detail tabs appear below. Click the \"Layer Data\" tab."
        },
        {
          "title": "View the tab's data",
          "caption": "Switch tabs to view the project's data."
        }
      ]
    },
    "t-member": {
      "title": "Members and permissions",
      "steps": [
        {
          "title": "Open your profile",
          "caption": "Click your profile at the bottom of the left rail."
        },
        {
          "title": "Open \"Project Permission Management\"",
          "caption": "My Page opens. Click \"Project Permission Management\"."
        },
        {
          "title": "Pick a project, click \"Add Member\"",
          "caption": "In \"Select Project\", choose a project you own to see its member list. Click \"Add Member\"."
        },
        {
          "title": "Enter an email",
          "caption": "Enter the email of the person to invite."
        },
        {
          "title": "Click \"Add\"",
          "caption": "Enter the email and click \"Add\"."
        },
        {
          "title": "Change the role",
          "caption": "\"User has been added\" appears and the new member is added to the list. Choose a role."
        },
        {
          "title": "Click \"Save\"",
          "caption": "Changing a role turns on \"Save\" at the bottom. Changes apply only after you click \"Save\"."
        },
        {
          "title": "Check the save",
          "caption": "\"Permission has been updated\" appears."
        }
      ]
    },
    "t-history": {
      "title": "Project history",
      "note": "Scroll to the end of the list to load older records. Times follow your <a href=\"#t-lang\">display timezone</a>.",
      "steps": [
        {
          "title": "Open your profile",
          "caption": "Project history gathers who added, edited or deleted what and when, project by project. Click your profile at the bottom of the left rail."
        },
        {
          "title": "Open \"Project History\"",
          "caption": "My Page opens. Click \"Project History\"."
        },
        {
          "title": "Click \"Filter\"",
          "caption": "Records of the project chosen in \"Select Project\" appear by date, newest first. Records from a time series show the shooting date on the right. Click \"Filter\"."
        },
        {
          "title": "Choose \"Annotation\"",
          "caption": "The record types appear. Choose \"Annotation\"."
        },
        {
          "title": "See annotation records",
          "caption": "Only the records of annotations being added, edited or deleted remain."
        }
      ]
    },
    "t-lang": {
      "title": "Language and display timezone",
      "note": "The language you choose is saved in this browser and stays the same next time. The display timezone has no setting to change; it follows your browser's timezone, and times in <a href=\"#t-history\">Project history</a> use it too.",
      "steps": [
        {
          "title": "Click the language button",
          "caption": "You can show the screen in Korean, English, Japanese or Khmer. Click \"ko\", the current language, at the bottom of the left rail."
        },
        {
          "title": "Choose \"日本語\"",
          "caption": "The four languages appear. Here, choose \"日本語\"."
        },
        {
          "title": "Open your profile",
          "caption": "Menus and buttons switch to Japanese, and the language button now reads \"ja\". Click your profile to see the display timezone."
        },
        {
          "title": "Check the display timezone",
          "caption": "In My Page, the display timezone (\"表示タイムゾーン\") under account information (\"アカウント情報\") is set automatically from your browser's timezone."
        }
      ]
    },
    "t-upload": {
      "title": "Upload drone images",
      "note": "Photos with the same file name are uploaded only once, even from different folders, so make sure photos from different flights don't share names.",
      "steps": [
        {
          "title": "Open the \"Drone Images\" tab",
          "caption": "Select a project and a capture date to open the details below. Click the \"Drone Images\" tab."
        },
        {
          "title": "Click \"Add Images\"",
          "caption": "Uploaded photos are listed in \"Uploaded List\". Click \"Add Images\"."
        },
        {
          "title": "Pick photos with \"Select File\"",
          "caption": "Drag and drop .jpg or .jpeg files, or choose them with \"Select File\"."
        },
        {
          "title": "Click \"Upload Images\"",
          "caption": "The number of target images and their total size are shown. Click \"Upload Images\"."
        },
        {
          "title": "Check the upload",
          "caption": "\"Images uploaded successfully.\" appears, and the uploaded photos show in \"Uploaded List\"."
        }
      ]
    },
    "t-mapping": {
      "title": "Mapping requests and GCPs",
      "steps": [
        {
          "title": "Click \"GCP Upload\"",
          "caption": "Select a capture date and look at \"$t(projects:timeSeries.mappingInfo)\" in the \"Information\" tab. First, click \"GCP Upload\"."
        },
        {
          "title": "Turn on \"$t(projects:timeSeries.gcpAutoDetect)\"",
          "caption": "Choose a GCP file ($t(projects:timeSeries.gcpGuide.readableFormats)) and its file name appears in the GCP row."
        },
        {
          "title": "Open the auto-detect guide",
          "caption": "If you laid standard markers before the flight, turn on \"$t(projects:timeSeries.gcpAutoDetect)\". The \"?\" on the right opens the auto-detect guide."
        },
        {
          "title": "Close the guide",
          "caption": "Check the conditions and the marker print files in the \"$t(projects:timeSeries.gcpGuide.title)\". Click close."
        },
        {
          "title": "Click \"Request Mapping\"",
          "caption": "Click \"Request Mapping\"."
        },
        {
          "title": "Confirm with \"Request\"",
          "caption": "A confirmation window opens. After the request, you can no longer upload or delete images. Click \"Request\"."
        },
        {
          "title": "Check the request",
          "caption": "\"The mapping request has been submitted.\" appears and the button changes to \"Request Completed\"."
        }
      ]
    },
    "t-download": {
      "title": "Download deliverables",
      "note": "If you need height values, use the elevation points in the CAD package instead of the DSM GeoTIFF.",
      "steps": [
        {
          "title": "Click \"Generate\"",
          "caption": "This is the \"Download\" box to the right of \"$t(projects:timeSeries.mappingInfo)\" for a processed capture date. Each resolution has \"Generate\" or \"Download\"."
        },
        {
          "title": "Click \"Download\"",
          "caption": "After you click \"Generate\", the button shows \"Generating N%\" and then changes to \"Download\"."
        },
        {
          "title": "Approve the download",
          "caption": "Large files over 1 GB go through an extra \"Confirm download\" window."
        }
      ]
    },
    "t-layerdata": {
      "title": "Layer data",
      "note": "Turn uploaded layers on and off in the <ui>LAYER</ui> panel of the 2D and 3D screens.",
      "steps": [
        {
          "title": "Open the \"Layer Data\" tab",
          "caption": "Select a project and click the \"Layer Data\" tab."
        },
        {
          "title": "Click the upload icon",
          "caption": "There are GIS, Line Layer, BIM and Point Cloud boxes. Click the upload icon on a box header."
        },
        {
          "title": "Check the upload",
          "caption": "An upload-complete notice appears at the top right, and the file appears in the GIS box."
        }
      ]
    },
    "t-terrain": {
      "title": "Terrain elevation data",
      "note": "The cross-section quantity table is available only when both original ground level and design height are present.",
      "steps": [
        {
          "title": "Open \"Terrain Elevation Data\"",
          "caption": "Select a project and click the \"Terrain Elevation Data\" tab."
        },
        {
          "title": "Click the upload icon",
          "caption": "There is a \"Drawing\" box. Click the upload icon on the box header."
        },
        {
          "title": "Choose the alignment DXF",
          "caption": "In the \"Upload drawings\" window, pair each drawing with its alignment DXF and click \"Upload\"."
        },
        {
          "title": "Click \"Upload\"",
          "caption": "When you choose the alignment DXF, its file name appears next to the drawing and \"Upload\" becomes available."
        },
        {
          "title": "Check the upload",
          "caption": "An upload-complete notice appears at the top right, and the drawing appears in the \"Drawing\" box."
        }
      ]
    },
    "t-ref": {
      "title": "Reference data",
      "steps": [
        {
          "title": "Open the \"Reference Data\" tab",
          "caption": "Select a project and click the \"Reference Data\" tab."
        },
        {
          "title": "Click the document upload icon",
          "caption": "There are image, document and video boxes. Click the upload icon on the \"Documents\" box header."
        },
        {
          "title": "Check the upload",
          "caption": "An upload-complete notice appears at the top right, and the file appears in the \"Documents\" box."
        }
      ]
    },
    "t-2dlayer": {
      "title": "Turn layers on and off",
      "note": "Upload GIS and line layers in <a href=\"#t-layerdata\">Layer data</a>.",
      "steps": [
        {
          "title": "Open \"LAYER\"",
          "caption": "Click \"LAYER\" in the left rail of the 2D inspection screen."
        },
        {
          "title": "Turn off \"Annotation\"",
          "caption": "The \"LAYER\" panel opens. The \"Annotation\" switch at the top shows or hides issues on the map."
        },
        {
          "title": "Expand \"GIS Layer\"",
          "caption": "Issues are now hidden. Expand \"GIS Layer\"."
        },
        {
          "title": "Turn on the cadastral map",
          "caption": "Your uploaded GIS layers appear. Turn on the cadastral map switch."
        },
        {
          "title": "Expand \"Linear Layer\"",
          "caption": "Parcel boundaries are overlaid on the map. Expand \"Linear Layer\"."
        },
        {
          "title": "Turn on the planned route",
          "caption": "Turn on the planned route switch."
        },
        {
          "title": "View the drawn route",
          "caption": "The planned route is drawn on the map."
        },
        {
          "title": "Turn on \"DSM\"",
          "caption": "Turn on \"DSM\" under \"Primary Layer\"."
        },
        {
          "title": "View elevation colors",
          "caption": "DSM elevation colors replace the orthomosaic. Only one of the orthomosaic and DSM can be on at a time."
        }
      ]
    },
    "t-folder": {
      "title": "Group layers into folders",
      "note": "Hold ⌘ (Ctrl on Windows) while clicking to add layers that are not next to each other one at a time. Line layers and the model and layer lists in the 3D screen are grouped the same way, and only <a href=\"#t-member\">owners and editors</a> can create folders and move items.",
      "steps": [
        {
          "title": "Open \"LAYER\"",
          "caption": "Grouping several GIS layers into one folder shortens the list and lets you turn the whole folder on or off. Click \"LAYER\" in the left rail of the 2D inspection screen."
        },
        {
          "title": "Expand \"GIS Layer\"",
          "caption": "The \"LAYER\" panel opens. Expand \"GIS Layer\"."
        },
        {
          "title": "Click the new folder button",
          "caption": "Your uploaded GIS layers appear. Click the new folder button next to the search box."
        },
        {
          "title": "Enter a folder name",
          "caption": "A folder name box appears at the top of the list. Type a name and press Enter."
        },
        {
          "title": "Shift-click the first layer",
          "caption": "The underground utilities folder (\"지하 시설물\") is created. Now pick the layers to group: hold Shift and click the water main layer."
        },
        {
          "title": "Shift-click the last layer",
          "caption": "The water main is selected, and the number selected appears above the list. Hold Shift and click the gas main layer."
        },
        {
          "title": "Drag into the folder",
          "caption": "Every layer from the water main to the gas main is now selected. Drag one of them onto the underground utilities folder."
        },
        {
          "title": "Expand the folder",
          "caption": "The selected layers move into the folder together, and the list gets shorter. Click the arrow in front of the folder to expand it."
        },
        {
          "title": "Turn on the folder switch",
          "caption": "The pipe layers are inside the folder. Turn on the switch on the folder row."
        },
        {
          "title": "Check the map",
          "caption": "All layers in the folder turn on at once, and the pipes are drawn along the road on the map."
        }
      ]
    },
    "t-compare": {
      "title": "Compare time series",
      "steps": [
        {
          "title": "Click \"2-pane view\"",
          "caption": "In the split-view group at the top left of the map, click \"2-pane view\"."
        },
        {
          "title": "Open the right pane's date",
          "caption": "The screen splits in two. Click the date above the right pane."
        },
        {
          "title": "Choose a date to compare",
          "caption": "Only dates whose 2D processing is finished appear. Choose the date to compare."
        },
        {
          "title": "Click \"4-pane view\"",
          "caption": "The same spot during excavation and after restoration appears side by side. Now click \"4-pane view\"."
        },
        {
          "title": "Open the lower-right pane's date",
          "caption": "The screen splits into four. Click the lower-right pane's date."
        },
        {
          "title": "Choose a date",
          "caption": "Choose the date to show in this pane."
        },
        {
          "title": "Drag one pane",
          "caption": "Each pane shows the date you choose for it. Drag one pane."
        },
        {
          "title": "See the panes move together",
          "caption": "All panes move together."
        }
      ]
    },
    "t-issue": {
      "title": "Create and edit issues",
      "note": "If you know the coordinates, you can also create an issue by entering its position (x-y or lon/lat) in <ui>ISSUE</ui> → <ui>Create</ui>.",
      "steps": [
        {
          "title": "Select the \"Point\" tool",
          "caption": "In the drawing tools, select \"Point\" under \"Creation\"."
        },
        {
          "title": "Place a point on the map",
          "caption": "Click the map as the guide chip next to the cursor shows."
        },
        {
          "title": "Click \"Edit vertices\"",
          "caption": "It is saved as an issue right away, with the \"Annotation Creation Completed\" alert. Click \"Edit vertices\" under \"Edit\"."
        },
        {
          "title": "Drag the point",
          "caption": "Drag the point to move it."
        },
        {
          "title": "Check the new position",
          "caption": "It is saved at the new position right away."
        }
      ]
    },
    "t-issueinfo": {
      "title": "Find issues and comment",
      "steps": [
        {
          "title": "Open \"ISSUE\"",
          "caption": "Click \"ISSUE\" in the left rail."
        },
        {
          "title": "Open \"Filter\"",
          "caption": "Issues are grouped by type. Click \"Filter\"."
        },
        {
          "title": "Remove a condition",
          "caption": "All conditions start selected, and clicking one removes it. Click \"Marker\" to remove it."
        },
        {
          "title": "Click \"Confirmation\"",
          "caption": "\"Marker\" is removed. Click \"Confirmation\"."
        },
        {
          "title": "Expand a type header",
          "caption": "Only matching issues remain. Click a type header to expand its issues."
        },
        {
          "title": "Click \"Fly to issue\"",
          "caption": "Click the location icon (\"Fly to issue\") on the row."
        },
        {
          "title": "Scroll down the panel",
          "caption": "The map moves to the issue and \"Issue Information\" opens with the grade trend by date. Scroll down the panel."
        },
        {
          "title": "Open the comments",
          "caption": "Click \"2 Comments\" at the bottom."
        },
        {
          "title": "Write a comment",
          "caption": "This is the comment window, where comments with photos also appear. Type a comment in the input."
        },
        {
          "title": "Click the send button",
          "caption": "Click the send button."
        },
        {
          "title": "Check the new comment",
          "caption": "The comment is posted right away."
        }
      ]
    },
    "t-measure": {
      "title": "Issue measurements",
      "note": "Use the download icons next to the section titles to save measurement, volume and coordinate values as CSV files.",
      "steps": [
        {
          "title": "Click a polygon issue",
          "caption": "Click a polygon issue on the map."
        },
        {
          "title": "Click the \"Measurement\" tab",
          "caption": "\"Issue Information\" opens. Click the \"Measurement\" tab."
        },
        {
          "title": "Open the date field",
          "caption": "A polygon issue shows its horizontal area, 3D area and surface area. Click the date field at the top."
        },
        {
          "title": "Choose a date to compare",
          "caption": "The dates this issue exists on appear. Choose a date to compare."
        },
        {
          "title": "Scroll down the panel",
          "caption": "A measurement card is added for each date, and the comparison card below shows the difference. Scroll down the panel."
        },
        {
          "title": "Enter the floor height",
          "caption": "For volume, enter the floor height."
        },
        {
          "title": "Click \"Calculate\"",
          "caption": "Click \"Calculate\"."
        },
        {
          "title": "View volume by date",
          "caption": "For each date, the part above the base surface is the Cut Volume and the part below is the Fill Volume."
        },
        {
          "title": "Click a line issue",
          "caption": "Now click a line issue on the map."
        },
        {
          "title": "Click the \"Measurement\" tab",
          "caption": "Click the \"Measurement\" tab."
        },
        {
          "title": "Scroll down the panel",
          "caption": "A line issue shows its horizontal, 3D and surface distances. Scroll down the panel."
        },
        {
          "title": "Expand \"Line\"",
          "caption": "Expand \"Line\" at the bottom."
        },
        {
          "title": "View values per segment",
          "caption": "Each segment shows its distances, elevation difference and slope."
        }
      ]
    },
    "t-issueexport": {
      "title": "Export issues",
      "note": "If you take a date out of <ui>Selected Time Series</ui> on the right and click <ui>Confirm</ui>, the issue on that date is deleted.",
      "steps": [
        {
          "title": "Click an issue",
          "caption": "Click the issue you want to follow on other dates."
        },
        {
          "title": "Scroll down the panel",
          "caption": "\"Issue Information\" opens, and its grade trend has only one date. Scroll down the panel."
        },
        {
          "title": "Click \"Export Issue\"",
          "caption": "Click \"Export Issue\" at the bottom."
        },
        {
          "title": "Choose a date",
          "caption": "This is the \"Select Analysis Time Series\" window. In \"Time Series List\" on the left, choose the dates to export to."
        },
        {
          "title": "Move with \">\"",
          "caption": "Use \">\" in the middle to move them to \"Selected Time Series\" on the right. \"»\" moves all dates before or after the base date at once."
        },
        {
          "title": "Click \"Confirm\"",
          "caption": "Click \"Confirm\"."
        },
        {
          "title": "Check the alert",
          "caption": "The \"Issue linked successfully\" alert appears."
        },
        {
          "title": "View the longer grade trend",
          "caption": "The chosen dates are added to \"Grade Trend\", so you can follow the same issue by date."
        }
      ]
    },
    "t-droneimg": {
      "title": "View original drone photos",
      "steps": [
        {
          "title": "Select the \"Drone image\" tool",
          "caption": "Under \"Others\" in the drawing tools, click \"Drone image\" (camera icon)."
        },
        {
          "title": "Click the map",
          "caption": "Click the spot on the map you want to see photos of."
        },
        {
          "title": "Choose another thumbnail",
          "caption": "\"Image List\" below shows the original photos taken within 100 m of that spot. Click another thumbnail."
        },
        {
          "title": "Click fullscreen",
          "caption": "The card switches to that photo and the map marks where it was taken. Click the card's fullscreen button."
        },
        {
          "title": "Go to the next photo",
          "caption": "The photo fills the screen. Use the arrow below to go to the next photo."
        },
        {
          "title": "View the next photo",
          "caption": "The next photo appears."
        }
      ]
    },
    "t-geolog": {
      "title": "Photos and videos (GeoLog)",
      "steps": [
        {
          "title": "Open \"GEOLOG\"",
          "caption": "Click \"GEOLOG\" in the left rail."
        },
        {
          "title": "Click \"+\"",
          "caption": "This is the list of site photos and videos. Click \"+\" at the top."
        },
        {
          "title": "Choose \"Upload files\"",
          "caption": "From \"Upload files\", \"Upload folder\" and \"Upload onto map\", choose \"Upload files\"."
        },
        {
          "title": "Click \"Location\"",
          "caption": "The chosen photos upload right away. Photos without a location are marked \"No location\" and have no pin on the map. Click \"Location\"."
        },
        {
          "title": "Choose \"Set location\"",
          "caption": "Choose \"Set location\"."
        },
        {
          "title": "Choose a photo",
          "caption": "Choose the photo to place."
        },
        {
          "title": "Click \"Place on map\"",
          "caption": "Click \"Place on map\"."
        },
        {
          "title": "Click where it was taken",
          "caption": "Click the spot on the map where the photo was taken."
        },
        {
          "title": "Click \"Details\"",
          "caption": "A photo pin and a preview card appear there. Click \"Details\" on the card."
        },
        {
          "title": "View in the large window",
          "caption": "The large window shows the photo with its uploader, date and topic."
        }
      ]
    },
    "t-report": {
      "title": "Reports",
      "note": "A report includes every issue of the current date, regardless of the <ui>ISSUE</ui> filter.",
      "steps": [
        {
          "title": "Open \"REPORT\"",
          "caption": "Click \"REPORT\" in the left rail."
        },
        {
          "title": "Enter a title",
          "caption": "The author fields are filled with your information. Enter the report title."
        },
        {
          "title": "Click \"Export Report\"",
          "caption": "Click \"Export Report\"."
        },
        {
          "title": "Click the PDF download button",
          "caption": "The A4 preview opens. Click the PDF download button at the top to save it as a PDF file."
        }
      ]
    },
    "t-capture": {
      "title": "Coordinates and area capture",
      "steps": [
        {
          "title": "Click the map",
          "caption": "Click the spot on the map whose coordinates you want."
        },
        {
          "title": "Open the coordinate system menu",
          "caption": "The coordinate bar at the bottom right shows that spot's x, y and z. Click the coordinate system field on its left."
        },
        {
          "title": "Select \"Google Mercator\"",
          "caption": "Choose the coordinate system to show. Click \"Google Mercator\"."
        },
        {
          "title": "Check the new coordinates",
          "caption": "The coordinates switch to the chosen system."
        },
        {
          "title": "Select \"Area capture\"",
          "caption": "Now click \"Area capture\" under \"Others\" in the drawing tools."
        },
        {
          "title": "Read the area guide",
          "caption": "A guide asks you to drag the area to capture on the map."
        },
        {
          "title": "Drag to set the area",
          "caption": "Drag the area to capture on the map."
        },
        {
          "title": "Choose the output scale",
          "caption": "The ground size, output size and effective GSD appear. Choose the output scale."
        },
        {
          "title": "Click the PNG download button",
          "caption": "The output size becomes four times larger. Click the PNG download button to save it as a PNG file."
        }
      ]
    },
    "t-pf-dxf": {
      "title": "DXF sections (2D)",
      "note": "Upload linear layers (DXF) to the Line Layer box in <a href=\"#t-layerdata\">Layer data</a>.",
      "steps": [
        {
          "title": "Click \"LAYER\"",
          "caption": "In the left rail of the 2D inspection screen, click \"LAYER\"."
        },
        {
          "title": "Expand \"Linear Layer\"",
          "caption": "This is the layer panel. Expand \"Linear Layer\"."
        },
        {
          "title": "Click \"Open section graph\"",
          "caption": "The linear layer (DXF) you uploaded appears. Click the graph icon on the right of the row (\"Open section graph\")."
        },
        {
          "title": "Click \"Check\" for the longitudinal section",
          "caption": "The section settings panel opens, and the alignment and stations (0+000, 0+020 …) are drawn on the map. Click \"Check\" under \"Longitudinal\"."
        },
        {
          "title": "Click on the graph line",
          "caption": "The longitudinal section graph opens below. The horizontal axis is the distance from the start point and the vertical axis is the height. Click on the graph line."
        },
        {
          "title": "See the spot on the map",
          "caption": "The spot you clicked is marked with a dot on the alignment on the map, and the row below the graph shows its coordinates and height."
        },
        {
          "title": "Enter 80 as the end point",
          "caption": "Next, look at cross sections. Enter 80 as the end point under \"Cross section\" in the settings panel."
        },
        {
          "title": "Click \"Check\"",
          "caption": "Keep the width (10 m left and right) and the interval (20 m), and click \"Check\"."
        },
        {
          "title": "Click the next-section button",
          "caption": "The cross-section graph of the first station opens, and a cross-section line is drawn on the map at each station (the current one in red). Click the next-section button (›) in the graph header."
        },
        {
          "title": "Click a cross-section line on the map",
          "caption": "It moves to the next station (020). Clicking the 0+060 cross-section line on the map also switches to that station."
        },
        {
          "title": "Click \"Save\"",
          "caption": "The graph changes to that station (060). To keep this range, click \"Save\"."
        },
        {
          "title": "See the saved notice",
          "caption": "\"The cross-section has been saved.\" appears."
        },
        {
          "title": "Click the saved range card",
          "caption": "The \"Saved Cross Sections\" list expands. Click the card of the saved range (0 ~ 080)."
        },
        {
          "title": "Enter station 50",
          "caption": "The range details and the station list appear. Enter 50 in the field below."
        },
        {
          "title": "Click \"Add Special Cross Section\"",
          "caption": "Click \"Add Special Cross Section\" (+) on the right."
        },
        {
          "title": "See the added notice",
          "caption": "\"Special cross section has been added.\" appears."
        },
        {
          "title": "Click station 050",
          "caption": "Station 050 is added to the list. Click 050."
        },
        {
          "title": "View from station 050",
          "caption": "The graph of the saved range opens at station 050."
        }
      ]
    },
    "t-pf-dxf3d": {
      "title": "DXF sections (3D)",
      "steps": [
        {
          "title": "Click \"LAYER\"",
          "caption": "In the left rail of the 3D screen, click \"LAYER\"."
        },
        {
          "title": "Expand \"Linear Layer\"",
          "caption": "This is the 3D layer panel. Expand \"Linear Layer\"."
        },
        {
          "title": "Click \"Open section graph\"",
          "caption": "Click the graph icon on the right of the row (\"Open section graph\")."
        },
        {
          "title": "Click \"Check\" for the longitudinal section",
          "caption": "The same section settings panel as in 2D opens. Click \"Check\" under \"Longitudinal\"."
        },
        {
          "title": "Click \"Check\" for the cross section",
          "caption": "Stations appear on the model, and the longitudinal section graph opens below. Click \"Check\" under \"Cross section\"."
        },
        {
          "title": "Click a cross-section line on the model",
          "caption": "A cross-section line is drawn on the model at each station, and the cross-section graph of the first station opens. Click the 0+040 cross-section line on the model."
        },
        {
          "title": "View that station's cross section",
          "caption": "The graph below changes to the cross section of that station (040)."
        }
      ]
    },
    "t-pf-export": {
      "title": "Export DXF sections",
      "note": "You can export only a saved range. First save the range in <a href=\"#t-pf-dxf\">DXF sections (2D)</a>.",
      "steps": [
        {
          "title": "Click \"Saved Cross Sections\"",
          "caption": "Export the cross sections of a saved range as files. Under \"Cross section\", click \"Saved Cross Sections\"."
        },
        {
          "title": "Click \"Export\"",
          "caption": "Click the export icon (\"Export\") on the right of the saved range card."
        },
        {
          "title": "Click \"Shooting Date\"",
          "caption": "This is the \"Export Cross Section Data\" window. The range, width and interval are shown at the top. Click \"Shooting Date\" to choose the dates to include."
        },
        {
          "title": "Choose a capture date",
          "caption": "This is the capture date list. You can choose several dates together. Choose 2024/01/19."
        },
        {
          "title": "Click \"Create\"",
          "caption": "The chosen date is filled in. There is only one drawing, so \"Ground Level\" and \"Design Height\" are already chosen. Click \"Create\"."
        },
        {
          "title": "See the creation notice",
          "caption": "\"File creation started.\" appears at the top right."
        },
        {
          "title": "Close the window",
          "caption": "A \"Creating\" file appears in the list below. The list refreshes when you open the window again. Close the window."
        },
        {
          "title": "Click \"Export\" again",
          "caption": "When the file is about ready, click \"Export\" again."
        },
        {
          "title": "Select the finished file",
          "caption": "The list has refreshed and the status is now \"Done\". Click the checkbox in front of the file to select it."
        },
        {
          "title": "Click \"Download\"",
          "caption": "The file is selected. Click \"Download\"."
        },
        {
          "title": "Choose the CSV format",
          "caption": "Choose the format to download (CSV, DXF, Earthwork Table, Earthwork Station Quantity). Click CSV."
        },
        {
          "title": "See the download notice",
          "caption": "\"CSV file has been downloaded.\" appears at the top right."
        }
      ]
    },
    "t-pf-issue": {
      "title": "Line issue sections (2D)",
      "steps": [
        {
          "title": "Click a line issue",
          "caption": "Click a line issue on the 2D map."
        },
        {
          "title": "Click the \"Measurement\" tab",
          "caption": "\"Issue Information\" opens. Click the \"Measurement\" tab."
        },
        {
          "title": "Click \"Check Elevation Profile\"",
          "caption": "Below the line length are the profile and cross-section settings. Keep the segment range (P1–P3) of the profile and click \"Check Elevation Profile\"."
        },
        {
          "title": "Scroll the panel down",
          "caption": "A longitudinal section graph cut along the issue line opens below. Now look at cross sections. Scroll the \"Issue Information\" panel down."
        },
        {
          "title": "Click \"Check Cross Section\"",
          "caption": "In the cross-section settings, set the segment range, start station, interval and width, then click \"Check Cross Section\"."
        },
        {
          "title": "View cross sections along the issue line",
          "caption": "Cross sections open at each interval along the issue line, and a cross-section line is drawn on the map at each station."
        }
      ]
    },
    "t-pf-issue3d": {
      "title": "Line issue sections (3D)",
      "steps": [
        {
          "title": "Click \"ISSUE\"",
          "caption": "You can open sections from a line issue on the 3D screen too. Click \"ISSUE\" in the left rail."
        },
        {
          "title": "Expand a type group",
          "caption": "Issues are grouped by type. Expand the type that has the line issue."
        },
        {
          "title": "Click \"Fly to issue\"",
          "caption": "Click \"Fly to issue\" in the line issue's row."
        },
        {
          "title": "Tilt with the right button",
          "caption": "The camera moves above the issue and looks straight down, and \"Issue Information\" opens. Hold the right button and drag up to tilt the model."
        },
        {
          "title": "Click the \"Measurement\" tab",
          "caption": "Now you see the model at an angle. Click the \"Measurement\" tab."
        },
        {
          "title": "Click \"Check Elevation Profile\"",
          "caption": "The same profile and cross-section settings as in 2D appear. Click \"Check Elevation Profile\"."
        },
        {
          "title": "See the graph in 3D",
          "caption": "A longitudinal section graph cut along the issue line opens below, and the vertex numbers (1, 2, 3) are marked on the model."
        }
      ]
    },
    "t-pf-3d": {
      "title": "3D section measure tool",
      "steps": [
        {
          "title": "Click \"Profile\"",
          "caption": "On the 3D inspection screen, you can draw a line directly on the model to see its section. In the \"Calc\" tools on the left, click \"Profile\"."
        },
        {
          "title": "Place the first point",
          "caption": "Click on the model to place the first point of the line. Place the points along the excavation."
        },
        {
          "title": "Place the next point",
          "caption": "Place the next point."
        },
        {
          "title": "Double-click to finish",
          "caption": "Place the last point with a double-click to finish."
        },
        {
          "title": "See the graph",
          "caption": "A longitudinal section graph cut along the drawn line opens below."
        }
      ]
    },
    "t-pf-view": {
      "title": "Using the graph (1) Controls",
      "steps": [
        {
          "title": "Turn on \"Partial Mode\"",
          "caption": "This is the graph opened in \"Line issue sections (3D)\". For a line with three or more vertices, \"Partial Mode\" shows one segment at a time. Turn on \"Partial Mode\"."
        },
        {
          "title": "Click \"Next leg\"",
          "caption": "Only the first segment (P1–P2) appears in the graph. Click \"Next leg\"."
        },
        {
          "title": "Scroll to zoom in",
          "caption": "It moves to the next segment (P2–P3). Scrolling over the graph zooms in around the cursor."
        },
        {
          "title": "Drag to pan",
          "caption": "The graph is zoomed in. Drag the graph sideways."
        },
        {
          "title": "Click \"More tools\"",
          "caption": "The graph has moved. Tools that do not fit in the header are gathered in \"More tools\". Click \"More tools\"."
        },
        {
          "title": "Click \"Reset view\"",
          "caption": "Click \"Reset view\" to go back to the first range."
        },
        {
          "title": "Drag the handle to enlarge the graph",
          "caption": "The graph is back to the first range. Drag the handle at the top of the panel up to make the graph area larger."
        },
        {
          "title": "Click \"More tools\"",
          "caption": "The graph area is larger. Click \"More tools\" again."
        },
        {
          "title": "Drag the \"Opacity\" slider",
          "caption": "Drag the \"Opacity\" slider to the right."
        },
        {
          "title": "Restore \"Opacity\"",
          "caption": "The graph panel becomes see-through and the 3D model behind it shows. Drag the slider back to the left end."
        },
        {
          "title": "Click \"Dark theme\"",
          "caption": "The panel is opaque again. Click \"Dark theme\" in the same menu."
        },
        {
          "title": "Click \"Fullscreen\"",
          "caption": "The background turns dark. Click \"Fullscreen\" at the top right of the graph to see only the graph on the whole screen."
        },
        {
          "title": "Click on the graph line",
          "caption": "Clicking on the graph line pins that spot, and the row below shows its X, Y and Z coordinates."
        },
        {
          "title": "Click the coordinate system field",
          "caption": "Click the coordinate system field (Korea2000)."
        },
        {
          "title": "Choose \"WGS84\"",
          "caption": "Choose \"WGS84\" from Google Mercator, WGS84 and UTM."
        },
        {
          "title": "Click \"Copy coordinates\"",
          "caption": "The values change to longitude, latitude and height. Click \"Copy coordinates\" on the right."
        },
        {
          "title": "See \"Copied\"",
          "caption": "\"Copied\" appears and the coordinates are copied."
        }
      ]
    },
    "t-pf-layers": {
      "title": "Using the graph (2) Compare layers",
      "note": "The drawing surfaces in the \"Design\" group appear when you upload a drawing in <a href=\"#t-terrain\">Terrain elevation data</a>.",
      "steps": [
        {
          "title": "Click \"Layers\"",
          "caption": "This is the cross-section graph. Click \"Layers\" (4) at the top left."
        },
        {
          "title": "Expand \"Terrain mesh\"",
          "caption": "There are Terrain mesh, BIM, Point Cloud, Design, Stratum and Vector groups. Expand \"Terrain mesh\"."
        },
        {
          "title": "Turn on another capture date",
          "caption": "There is a mesh for each capture date. Turn on 2024-01-25."
        },
        {
          "title": "Scroll the list down",
          "caption": "The sections of the two capture dates overlap in different colors (on 01-25 the excavation is still covered). Scroll the list down."
        },
        {
          "title": "Expand \"Design\"",
          "caption": "The \"Design\" group is further down. Expand \"Design\"."
        },
        {
          "title": "Turn on \"Design Height\"",
          "caption": "The drawing surfaces (original ground level and design height) are listed. Turn on \"Design Height\"."
        },
        {
          "title": "Turn on \"Original Ground Level\"",
          "caption": "The design excavation bottom (design height) is drawn. Turn on \"Original Ground Level\" too."
        },
        {
          "title": "Click \"Original\"",
          "caption": "The ground before excavation (original ground level) now shows with the current state. Under \"Color mode\", click \"Original\"."
        },
        {
          "title": "View in real colors",
          "caption": "The current-state line is painted in the model's real colors."
        }
      ]
    },
    "t-pf-measure": {
      "title": "Using the graph (3) Measure",
      "steps": [
        {
          "title": "Click \"Line measure\"",
          "caption": "Measure the excavation in the cross section at station 040. Under \"Draw\", click \"Line measure\"."
        },
        {
          "title": "Click the top-left corner",
          "caption": "Click on the section line to place points. Click the top-left corner of the excavation."
        },
        {
          "title": "Place a point on the bottom",
          "caption": "Click the excavation bottom (just below the wall). A dashed line and length follow the cursor from the last point."
        },
        {
          "title": "Right-click to finish",
          "caption": "Right-click the graph to finish the measurement."
        },
        {
          "title": "Click \"Point measure\"",
          "caption": "The segment length (excavation depth) appears, with a coordinate box at each point. Click \"Point measure\"."
        },
        {
          "title": "Click the right end of the bottom",
          "caption": "Clicking a point on the section line shows its coordinates. Click the right end of the excavation bottom."
        },
        {
          "title": "Click \"Labels: Pin\"",
          "caption": "A coordinate box appears for the point. If the boxes cover the length, click \"Labels: Pin\" to switch to \"Hover\"."
        },
        {
          "title": "Click \"Grid measure\"",
          "caption": "The coordinate boxes now show only when you hover over a point, leaving just the length. Click \"Grid measure\"."
        },
        {
          "title": "Click the first corner",
          "caption": "Click the two ends of the rectangle's diagonal in turn. Click the top left of the right pipe."
        },
        {
          "title": "Click the opposite corner",
          "caption": "Click the bottom right of the pipe (the excavation bottom)."
        },
        {
          "title": "Click \"Circle measure\"",
          "caption": "The width, height and diagonal of the pipe appear. Click \"Circle measure\"."
        },
        {
          "title": "Click the circle center",
          "caption": "Click the center of the circle first. Click the middle of the left pipe."
        },
        {
          "title": "Click the circle edge",
          "caption": "Click the top of the pipe."
        },
        {
          "title": "Click \"Axes measure\"",
          "caption": "The radius (R) appears. Click \"Axes measure\"."
        },
        {
          "title": "Click a spot below the road",
          "caption": "It previews where the horizontal and vertical lines through the cursor meet the section line. Click below the road on the right of the excavation."
        },
        {
          "title": "Click \"Erase\"",
          "caption": "The distances between the meeting points appear. Click \"Erase\"."
        },
        {
          "title": "Click \"All\"",
          "caption": "\"Selection\" erases only the measurement you click, and \"All\" erases everything. Click \"All\"."
        },
        {
          "title": "View the cleared graph",
          "caption": "All measurements on the graph are erased."
        }
      ]
    },
    "t-pf-image": {
      "title": "Using the graph (4) Capture & export",
      "steps": [
        {
          "title": "Click \"Capture image\"",
          "caption": "To save the graph as an image file, click \"Capture image\"."
        },
        {
          "title": "Choose \"PNG\"",
          "caption": "Choose a format from PNG, JPG, JPEG and WEBP. Click \"PNG\"."
        },
        {
          "title": "Click \"Point interval export\"",
          "caption": "The graph image is downloaded as a PNG file. To save the height values as a file, click \"Point interval export\"."
        },
        {
          "title": "Enter 2 as the point interval",
          "caption": "The \"Export data\" window opens, and the graph line changes to a line joining points taken at each \"Point interval\". Enter 2 as the point interval."
        },
        {
          "title": "Choose the CSV format",
          "caption": "The line is redrawn through points 2 m apart. From the formats (TXT, CSV, DXF), click CSV."
        },
        {
          "title": "Click \"Confirm\"",
          "caption": "The confirmation window shows the layers and point interval that go into the file. Click \"Confirm\" to download the file."
        }
      ]
    },
    "t-pf-qty": {
      "title": "Using the graph (5) Quantity table",
      "steps": [
        {
          "title": "Click \"Quantity Table\"",
          "caption": "This cross section has the drawing's design height and original ground level turned on. Click \"Quantity Table\"."
        },
        {
          "title": "Click \"Excel Download\"",
          "caption": "This is the \"Cross Section Quantity Table\" window. It shows this station's original ground level, design height and current height, and the fill and cut areas as planned (blue) and progressed (green). Click \"Excel Download\" to download the table as a file."
        }
      ]
    },
    "t-camera": {
      "title": "Camera controls",
      "note": "Double-clicking moves you closer to that spot only when no tool is selected. As the guide at the bottom right shows, drag with the left button to pan, drag with the right button to rotate and tilt, and scroll to zoom.",
      "steps": [
        {
          "title": "Click \"3D\"",
          "caption": "In the left rail of the 2D inspection screen, click \"3D\"."
        },
        {
          "title": "Scroll to move closer",
          "caption": "The 3D model of the same capture date opens, seen from above. Scroll up to move closer."
        },
        {
          "title": "Tilt with the right button",
          "caption": "You are closer to the model. Hold the right button and drag up to tilt."
        },
        {
          "title": "Rotate with the right button",
          "caption": "Now you look down at an angle. Hold the right button and drag sideways to rotate."
        },
        {
          "title": "Pan with the left button",
          "caption": "The view has rotated. Hold the left button and drag to move the model."
        },
        {
          "title": "Click \"Background Color\"",
          "caption": "Under \"Etc\", click \"Background Color\"."
        },
        {
          "title": "Choose a background color",
          "caption": "A color list opens. Choose a background color."
        },
        {
          "title": "Double-click to move closer",
          "caption": "The background around the model changes to that color. Double-click a spot you want to see to move closer to it."
        },
        {
          "title": "Click \"TOP\"",
          "caption": "You are close to that spot. Click \"TOP\" on the view cube at the top right."
        },
        {
          "title": "View from above",
          "caption": "The view switches to looking straight down."
        }
      ]
    },
    "t-fly": {
      "title": "First-person flight",
      "steps": [
        {
          "title": "Click \"Switch to first-person\"",
          "caption": "At the bottom right, click \"Switch to first-person\"."
        },
        {
          "title": "Turn page 1 of the controls",
          "caption": "The \"First-person controls\" window opens. Move with W·A·S·D, and double-tap to sprint. Click \"Next\"."
        },
        {
          "title": "Turn page 2 of the controls",
          "caption": "Page 2 — press Space to go up and Shift to go down. Click \"Next\"."
        },
        {
          "title": "Click \"Start\"",
          "caption": "Page 3 — scroll to zoom, and right-click to exit. Click \"Start\"."
        },
        {
          "title": "Hold W to move forward",
          "caption": "You are in first-person view. A crosshair appears in the center and a key guide at the bottom right. Move the mouse to look around, and hold W to move forward."
        },
        {
          "title": "Right-click to exit",
          "caption": "You have moved forward. Right-click (or press Esc) to exit first-person view."
        },
        {
          "title": "Back to normal controls",
          "caption": "You are back to the normal drag and scroll controls."
        }
      ]
    },
    "t-3dlayer": {
      "title": "3D layers",
      "note": "Upload GIS, line, BIM and Point Cloud layers in <a href=\"#t-layerdata\">Layer data</a>.",
      "steps": [
        {
          "title": "Open \"LAYER\"",
          "caption": "In the left rail, click \"LAYER\"."
        },
        {
          "title": "Turn on \"DSM\"",
          "caption": "This is the 3D layer panel. Turn on \"DSM\"."
        },
        {
          "title": "View height colors",
          "caption": "The model is colored by height, and a Lowest–Highest legend appears under DSM."
        },
        {
          "title": "Turn off \"DSM\"",
          "caption": "Turn off \"DSM\"."
        },
        {
          "title": "Drag the opacity slider",
          "caption": "Drag the \"Opacity\" slider of \"Mesh\" to make the model semi-transparent."
        },
        {
          "title": "Restore the opacity",
          "caption": "The model is semi-transparent, so you can see through it. Drag the slider back to the left end."
        },
        {
          "title": "Turn on \"Annotation\"",
          "caption": "The model is opaque again. Turn on \"Annotation\"."
        },
        {
          "title": "View issues",
          "caption": "Issues are drawn on the model."
        },
        {
          "title": "Expand \"Linear Layer\"",
          "caption": "Expand \"Linear Layer\"."
        },
        {
          "title": "Turn on the planned route",
          "caption": "This is the list of linear layers. Turn on the planned route."
        },
        {
          "title": "View the drawn route",
          "caption": "The planned route is drawn on the model."
        },
        {
          "title": "Click \"Fly to model\"",
          "caption": "Click the target button (\"Fly to model\") to the right of \"Mesh\"."
        },
        {
          "title": "View the whole model",
          "caption": "The view moves to look down on the whole model."
        }
      ]
    },
    "t-measure3d": {
      "title": "Distance and vertical measurement",
      "note": "Measurements are not saved and are cleared when you change the base time series.",
      "steps": [
        {
          "title": "Choose \"Distance\"",
          "caption": "Under \"Calc\", choose \"Distance\"."
        },
        {
          "title": "Place the first point",
          "caption": "Click on the model to place the first point. Each click adds a point, and a double-click finishes."
        },
        {
          "title": "Place the next point",
          "caption": "Place the next point. Each segment shows its 3D distance."
        },
        {
          "title": "Double-click to finish",
          "caption": "Place the last point with a double-click to finish."
        },
        {
          "title": "View segment distances",
          "caption": "The distance measurement is done. Each segment shows its 3D distance."
        },
        {
          "title": "Choose \"Vertical\"",
          "caption": "Now measure the depth of the excavation. Under \"Calc\", choose \"Vertical\"."
        },
        {
          "title": "Place the upper point",
          "caption": "Click the road surface beside the excavation."
        },
        {
          "title": "Place the lower point",
          "caption": "Click the bottom of the excavation to finish the measurement."
        },
        {
          "title": "View height, horizontal, and 3D distance",
          "caption": "The height (vertical), the horizontal distance, and the 3D distance between the two points appear together."
        }
      ]
    },
    "t-area3d": {
      "title": "Area and profile measurement",
      "note": "The profile graph is the same screen as the graph in the <a href=\"#c6\">Section graphs</a> chapter.",
      "steps": [
        {
          "title": "Choose \"Area\"",
          "caption": "Under \"Calc\", choose \"Area\"."
        },
        {
          "title": "Place the first vertex",
          "caption": "Click on the model to place the first vertex."
        },
        {
          "title": "Place the second vertex",
          "caption": "Place the next vertex."
        },
        {
          "title": "Place the third vertex",
          "caption": "An edge appears with its distance. Place the next vertex."
        },
        {
          "title": "Place the fourth vertex",
          "caption": "Once there are three vertices, the area appears. Place the next vertex."
        },
        {
          "title": "Double-click to finish",
          "caption": "Place the last vertex with a double-click to finish."
        },
        {
          "title": "View the area",
          "caption": "The area measurement is done. The 3D and plane areas appear in the middle, and each edge shows its distance."
        },
        {
          "title": "Choose \"Profile\"",
          "caption": "Choose \"Profile\"."
        },
        {
          "title": "Place the start point",
          "caption": "Click the start point of the line."
        },
        {
          "title": "Double-click the end point",
          "caption": "Double-click the end point."
        },
        {
          "title": "View the height graph",
          "caption": "A graph of heights measured along the line opens below. The excavation and pipes appear in section."
        },
        {
          "title": "Choose \"Measurement eraser\"",
          "caption": "Choose \"Measurement eraser\"."
        },
        {
          "title": "Click the measurement to erase",
          "caption": "Click the measurement to erase (the area)."
        },
        {
          "title": "Check the erased spot",
          "caption": "The area measurement is erased. Measurements are not saved, so they disappear when you reopen the screen."
        }
      ]
    },
    "t-issue3d": {
      "title": "3D issues",
      "note": "Lines, rectangles, polygons, and text are also created from \"Draw\": points and text finish on the first click, rectangles on the second click, and lines and polygons on a double-click. For an issue not in the current capture date, \"Fly to issue\" opens the \"Issue not in current time series\" window.",
      "steps": [
        {
          "title": "Choose the \"Point\" tool",
          "caption": "Under \"Draw\", choose \"Point\"."
        },
        {
          "title": "Click on the model",
          "caption": "If the issue layer was off, it turns on automatically with a notice. Click on the model where you want the issue."
        },
        {
          "title": "View issue information",
          "caption": "The issue is saved right away as \"Issue N\", and \"Issue Information\" opens on the left. Change its name, type, grade, and memo with \"Edit\"."
        },
        {
          "title": "Open \"ISSUE\"",
          "caption": "In the left rail, click \"ISSUE\"."
        },
        {
          "title": "Expand a type group",
          "caption": "Issues are grouped by type. Expand a type group."
        },
        {
          "title": "Click \"Fly to issue\"",
          "caption": "Click \"Fly to issue\" in the row of the issue you want to see."
        },
        {
          "title": "Click \"Show issue names\"",
          "caption": "The camera moves to that issue and \"Issue Information\" opens. Under \"Etc\", click \"Show issue names\"."
        },
        {
          "title": "View the hidden names",
          "caption": "Issue names on the model are hidden. Click again to show them."
        }
      ]
    },
    "t-clip": {
      "title": "Create clippings",
      "note": "Up to 8 clippings can be on at once. Rename one with the pencil button in the list and delete it with the minus button (deleted clippings cannot be restored).",
      "steps": [
        {
          "title": "Open \"CLIP\"",
          "caption": "In the left rail, click \"CLIP\"."
        },
        {
          "title": "Enter \"Side\"",
          "caption": "\"Clipping Tool\" opens, with \"Box\" selected under \"Add Clipping\". Enter the box side length (m) in \"Side\"."
        },
        {
          "title": "Enter a name",
          "caption": "Enter a name. If you leave it empty, a number is given, like \"Box 1\"."
        },
        {
          "title": "Click \"Add by clicking surface\"",
          "caption": "Click \"Add by clicking surface\"."
        },
        {
          "title": "Click the box position",
          "caption": "\"Click a model surface in the 3D view\" appears. Click the spot on the model that will be the center of the box. Right-click or Esc cancels."
        },
        {
          "title": "Turn it off with the switch",
          "caption": "Only the inside of the box remains, and the rest is hidden. The box appears in \"Clipping List\" and is saved right away. Use the list switch to turn a clipping on and off."
        },
        {
          "title": "Choose \"Plane\"",
          "caption": "The clipping is off, so the whole model is visible. Turning it back on moves the camera to it. Now choose \"Plane\"."
        },
        {
          "title": "Click \"Add by clicking surface\"",
          "caption": "Click \"Add by clicking surface\"."
        },
        {
          "title": "Click the plane position",
          "caption": "Click on the model where the plane should pass."
        },
        {
          "title": "View the horizontal plane",
          "caption": "A horizontal plane of the \"Side\" size appears at the clicked height, and only the part above it remains. \"Adjust and save clippings\" shows how to stand the plane up for a vertical section."
        }
      ]
    },
    "t-clipedit": {
      "title": "Adjust and save clippings",
      "note": "The \"Vertical\" joystick moves the clipping up and down, and the third joystick changes the height (\"Gap\") of a box or the size (\"Area\") of a plane.",
      "steps": [
        {
          "title": "Expand the plane",
          "caption": "Click the plane in the list to expand it."
        },
        {
          "title": "Click \"Edit\"",
          "caption": "\"Edges\" turns the clipping outline on and off, and \"Cap Fill\" colors the cut faces. \"Invert\" switches which side remains. Click \"Edit\"."
        },
        {
          "title": "Drag \"Tilt\"",
          "caption": "While editing, drag the vertices and faces in the 3D view directly, or use the joysticks and sliders below. Drag the \"Tilt\" slider all the way left to stand the plane up."
        },
        {
          "title": "Drag the \"Horizontal\" joystick",
          "caption": "The plane stands up and shows a section across the excavation. Hold and drag the \"Horizontal\" joystick to keep moving in that direction; release it to stop."
        },
        {
          "title": "View the moved section",
          "caption": "The section has moved along the excavation. The \"Rotation\" slider turns the plane."
        },
        {
          "title": "Click \"Save\"",
          "caption": "A changed clipping gets an orange dot next to its name, and \"Save\" turns on. Click \"Save\"."
        },
        {
          "title": "Check the save",
          "caption": "Saved. The orange dot disappears and editing closes. Before saving, \"Reset\" returns to the last saved shape, and while editing you can use \"Undo\" and \"Redo\"."
        }
      ]
    },
    "t-vector": {
      "title": "Vectors",
      "note": "Instead of latitude/longitude, you can also enter \"X/Y coordinates\" (project coordinate system). \"Show on map\" at the top of the panel hides or shows all vectors at once.",
      "steps": [
        {
          "title": "Open \"VECTOR\"",
          "caption": "In the left rail, click \"VECTOR\"."
        },
        {
          "title": "Click \"Create with coordinates\"",
          "caption": "This is the \"Vector\" panel. Click \"Create with coordinates\"."
        },
        {
          "title": "Enter a name",
          "caption": "The \"Create vector\" window opens. Enter a name."
        },
        {
          "title": "Choose \"Cylinder\"",
          "caption": "Choose the shape (Line, Cylinder, Square prism). Click \"Cylinder\"."
        },
        {
          "title": "Enter the radius",
          "caption": "Enter \"Radius (m)\". For a square prism, enter \"Side (m)\"."
        },
        {
          "title": "Enter coordinates",
          "caption": "For each point, enter the latitude, longitude, and height (m). Height is the orthometric height, and \"Add point\" adds more points (2 to 100)."
        },
        {
          "title": "Click \"Create\"",
          "caption": "Click \"Create\"."
        },
        {
          "title": "View the drawn cylinder",
          "caption": "The vector appears in the list, and a cylinder is drawn in the excavation in the 3D view."
        },
        {
          "title": "Expand the vector",
          "caption": "Click the vector to expand it."
        },
        {
          "title": "Click \"Cross-section\"",
          "caption": "Change the name, shape, radius, color, and memo in \"Info\" and the coordinates in \"Coordinates\", then click \"Save\". Click \"Cross-section\"."
        },
        {
          "title": "Enter the cut line length",
          "caption": "The \"Cross-section\" window opens. The cut position (distance from the start point) is set to the middle. Change \"Cut line length (m)\" to 8 m."
        },
        {
          "title": "Click \"Confirm\"",
          "caption": "Click \"Confirm\"."
        },
        {
          "title": "View the section graph",
          "caption": "The cut line is drawn, the camera moves above it, and a section graph opens below. The cylinder section is drawn over the model section, so you can compare the planned pipe with the actual pipe position."
        }
      ]
    },
    "t-members": {
      "title": "Connected workers and restricted zones",
      "steps": [
        {
          "title": "Click a worker row",
          "caption": "This is the work status screen. The \"MEMBERS\" (Connected workers) panel lists project participants, and people sending their location from the mobile app appear as pins on the map. Click the second row."
        },
        {
          "title": "Check the card",
          "caption": "The map moves to that worker and a card opens. It shows the role, last position report, GPS accuracy and violation status (now \"Clear\")."
        },
        {
          "title": "Click \"ZONES\"",
          "caption": "Let's make the excavation where this worker stands a restricted zone. Click \"ZONES\" in the left rail."
        },
        {
          "title": "Click \"Draw new restricted zone\"",
          "caption": "This is \"Manage restricted zones\". Each zone has an on/off switch, and the ⋯ menu renames or deletes it. Click \"Draw new restricted zone\"."
        },
        {
          "title": "Place the first vertex",
          "caption": "A drawing guide appears. Place the first vertex around the excavation."
        },
        {
          "title": "Place the second vertex",
          "caption": "Place the second vertex."
        },
        {
          "title": "Place the third vertex",
          "caption": "Place the third vertex."
        },
        {
          "title": "Double-click the last vertex",
          "caption": "Double-click to place the last vertex and finish drawing."
        },
        {
          "title": "Enter the zone name",
          "caption": "The \"New restricted zone name\" window opens. Enter a zone name."
        },
        {
          "title": "Click \"Confirm\"",
          "caption": "Click \"Confirm\"."
        },
        {
          "title": "Check the violation alert",
          "caption": "The zone is created, and a violation alert appears for the worker inside it."
        },
        {
          "title": "Check the new zone",
          "caption": "The zone is added to the list and drawn on the map. The worker's pin is inside the zone."
        },
        {
          "title": "Click \"MEMBERS\"",
          "caption": "Check it in the worker list as well. Click \"MEMBERS\"."
        },
        {
          "title": "Check the red row",
          "caption": "The worker's row turns red with \"Violation\" and the zone name. The violation status on the open card also changes to \"Violation\"."
        }
      ]
    },
    "t-equip": {
      "title": "Placing equipment",
      "note": "Equipment hidden from the map with the eye icon in the list is not counted for overlapping working radii (risk).",
      "steps": [
        {
          "title": "Click \"EQUIP\"",
          "caption": "In the left rail of the work status screen, click \"EQUIP\"."
        },
        {
          "title": "Click \"Add equipment\"",
          "caption": "The equipment panel shows the equipment already placed. Click \"Add equipment\"."
        },
        {
          "title": "Choose \"Excavator\"",
          "caption": "Categories (Lifting, Earthwork, Temporary, Material, Labor) and type cards open over the map. Click \"Excavator\"."
        },
        {
          "title": "Set the spec and click \"Place\"",
          "caption": "In the placement window, set the name, base spec, length, width, height, working radius and radius color, then click \"Place\"."
        },
        {
          "title": "Click where to place it",
          "caption": "The type cards dim and placement starts. Click next to the first excavator on the map. Right-click cancels."
        },
        {
          "title": "Check the overlapping radii",
          "caption": "The second excavator is placed. Where the two working radii overlap is shaded red, and a risk mark (!) appears on both rows in the list."
        },
        {
          "title": "Expand the new excavator",
          "caption": "Let's move it so the radii don't overlap. Click the expand button on the left of the new excavator's row."
        },
        {
          "title": "Click the \"Position & heading\" tab",
          "caption": "There are \"Spec\", \"Position & heading\" and \"Route\" tabs. Click \"Position & heading\"."
        },
        {
          "title": "Click \"Move on map\"",
          "caption": "The facing-direction dial and the longitude, latitude and height appear. Click \"Move on map\"."
        },
        {
          "title": "Click the new spot",
          "caption": "The button changes to \"Moving\". Click a spot on the map where the radii won't overlap."
        },
        {
          "title": "Check the new position",
          "caption": "The excavator moves there and its longitude and latitude change. The working radii no longer overlap, so the risk mark disappears."
        }
      ]
    },
    "t-route": {
      "title": "Equipment route simulation",
      "note": "Overlaps that change during playback are shown only on the map; the risk mark in the list is counted from the saved positions.",
      "steps": [
        {
          "title": "Expand the first excavator",
          "caption": "This is the equipment panel. The second excavator already has a route. Click the expand button on the left of the first excavator's row."
        },
        {
          "title": "Click the \"Route\" tab",
          "caption": "Click the \"Route\" tab."
        },
        {
          "title": "Click \"Draw route\"",
          "caption": "\"No route yet\" is shown. Click \"Draw route\"."
        },
        {
          "title": "Place the next point",
          "caption": "The equipment's position becomes the first point (\"Points\" 1). Click the next point on the map."
        },
        {
          "title": "Double-click the last point",
          "caption": "Double-click to place the last point and finish."
        },
        {
          "title": "Click \"Play route\"",
          "caption": "The route is saved, and \"Route simulation\" shows the playback bar, repeat and speed, plus the points, distance and estimated time. Click the play button."
        },
        {
          "title": "Expand the second excavator",
          "caption": "The first excavator moves along its route. To move both together, expand the second excavator's row."
        },
        {
          "title": "Click the \"Route\" tab",
          "caption": "Click the second excavator's \"Route\" tab."
        },
        {
          "title": "Click \"Play route\"",
          "caption": "Click the second excavator's play button."
        },
        {
          "title": "See the moment they overlap",
          "caption": "Both excavators move at once. When their working radii overlap, the overlap is shaded red and a risk mark (!) appears on their name tags."
        }
      ]
    },
    "t-ewzone": {
      "title": "Zone earthwork in 3D",
      "note": "Volumes are calculated from every cell in the zone; the section drawings in \"Calculation details\" show an example stretch of that calculation.",
      "steps": [
        {
          "title": "Click \"＋ Draw zone\"",
          "caption": "This is the earthwork screen. With \"3D voxel\", the earthwork volume is calculated by adding up every drawing that crosses a zone drawn on the 3D model. Click \"＋ Draw zone\"."
        },
        {
          "title": "Place the first vertex",
          "caption": "The panel shows \"Drawing a zone. Double-click to finish drawing.\" Place the first vertex around the excavation."
        },
        {
          "title": "Place the second vertex",
          "caption": "Place the second vertex."
        },
        {
          "title": "Place the third vertex",
          "caption": "Place the third vertex."
        },
        {
          "title": "Double-click the last vertex",
          "caption": "Double-click the last vertex to finish drawing."
        },
        {
          "title": "Click \"Calculate\"",
          "caption": "It is saved as \"Zone 1\", added to the list and painted on the model. \"Drawings used in Zone 1\" lists the drawings the zone crosses. Click \"Calculate\" on the \"Earthwork volume\" card."
        },
        {
          "title": "Expand \"Calculated time series\"",
          "caption": "When the calculation is done, coverage, \"Calculation details\" and \"View Earthwork Table\" appear. Expand \"Calculated time series 2/2\"."
        },
        {
          "title": "Uncheck \"2024-01-25\"",
          "caption": "The time series included in the calculation are listed. 01-25 was captured after the excavation was filled back in, so leave it out. Uncheck \"2024-01-25\"."
        },
        {
          "title": "Click \"Recalculate\"",
          "caption": "\"Excluded from the calculation\" appears, with a notice that the included time series have changed and \"Recalculate\". Click \"Recalculate\"."
        },
        {
          "title": "Click \"Calculation details\"",
          "caption": "The list changes to \"1/2\" and the notice goes away. Click \"Calculation details\"."
        },
        {
          "title": "Scroll down inside the window",
          "caption": "This is the \"Calculation details\" window. A section drawing of an example stretch shows how the difference between two surfaces is counted for each cell, with the planned volume (original ground → design height). Scroll down inside the window."
        },
        {
          "title": "Check completed and remaining",
          "caption": "For each time series, section drawings of the completed (original ground → drone ground) and remaining (drone ground → design height) volumes appear with the progress rate. Planned = completed + remaining."
        }
      ]
    },
    "t-ewtable": {
      "title": "Earthwork table",
      "note": "With several zones, each zone gets its own table. Drag tables to reorder them, and choose the zones to put in the file with \"Include in CSV export\".",
      "steps": [
        {
          "title": "Click \"View Earthwork Table\"",
          "caption": "Zone 1 is selected. Click \"View Earthwork Table\" on the \"Earthwork volume\" card."
        },
        {
          "title": "Drag the \"Opacity\" slider",
          "caption": "This is the \"Earthwork Table\" window. It shows cut (soil) and fill (subgrade, roadbed) by planned quantity, remaining earthwork and cumulative total by date, along with progress by capture date. Drag the \"Opacity\" slider to the right."
        },
        {
          "title": "See the model under the table",
          "caption": "The table becomes see-through and the 3D model and the zone below show. Drag the window header to move it, and download the table with \"Export CSV (1 zone)\"."
        }
      ]
    },
    "t-linear": {
      "title": "Linear-based earthwork",
      "note": "Section settings, graphs and the cross-section quantity table work the same as in <a href=\"#c6\">06 Section graphs</a>.",
      "steps": [
        {
          "title": "Click \"Linear-based\"",
          "caption": "\"Linear-based\" shows sections and quantities at each station along one alignment. Click \"Linear-based\" at the top of the earthwork panel."
        },
        {
          "title": "Click \"Open section graph\"",
          "caption": "The linear layer list appears, with the time series whose terrain surface the sections and quantity table are calculated from. Click the graph icon in a row (\"Open section graph\")."
        },
        {
          "title": "Click \"Check\" under \"Cross section\"",
          "caption": "The alignment and stations are shown on the model and the section settings panel opens. Click \"Check\" under \"Cross section\"."
        },
        {
          "title": "Drag the alignment to the middle",
          "caption": "Cross-section lines are drawn at each station and the cross-section graph of the first station opens below. Drag with the left button to move the alignment to the middle so the graph does not cover it."
        },
        {
          "title": "Tilt with the right button",
          "caption": "The view looks straight down. Hold the right button and drag up to tilt the model."
        },
        {
          "title": "Click the 0+060 cross-section line",
          "caption": "The model is seen at an angle. Click the 0+060 cross-section line on the model."
        },
        {
          "title": "Click \"More tools\"",
          "caption": "The graph changes to the cross section at that station (060). Click \"More tools\"."
        },
        {
          "title": "Click \"Quantity table\"",
          "caption": "Click \"Quantity table\"."
        },
        {
          "title": "View the quantity table",
          "caption": "This is the \"Cross-section quantity table\" window. It shows the original ground, design and current heights at this station, and fill and cut areas as planned (blue) and progress (green)."
        }
      ]
    },

  },
  "mapping": {
    "gcpPath": "Using GCPs (Ground Control Points) improves location accuracy. Upload your GCP file ($t(projects:timeSeries.gcpGuide.readableFormats)) in the GCP row of $t(projects:timeSeries.mappingInfo), then request mapping.",
    "autoDetect": "If [$t(projects:timeSeries.gcpAutoDetect)] appears in the GCP row, time series shot with DroneSquare standard markers laid out are mapped automatically, without manual work. The markers must be prepared before the flight, so check the guide below first.",
    "ownerOnly": "Only the project owner can request mapping.",
    "processingTime": "Processing time varies with the number of photos and the processing method (automatic or by our staff)."
  }
} as const;

export default landingManual;
